import {
  exactPin,
  missionTemplateReadResult,
  pinFields,
  readStatuses,
  samePin,
  type ExactPin,
  type MissionTemplateReadResult,
} from '../consumer/contract';

function keys(value: unknown, names: readonly string[]): asserts value is Record<string, unknown> {
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    Reflect.ownKeys(value).length !== names.length ||
    names.some((name) => !Object.hasOwn(value, name))
  )
    throw new Error('Invalid MissionTemplate wire fields');
}
function pin(value: unknown): ExactPin {
  keys(value, pinFields);
  if (pinFields.some((key) => typeof value[key] !== 'string')) throw new Error('Invalid pin text');
  return exactPin(value as ExactPin);
}
function bytes(value: unknown): readonly number[] {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)
  )
    throw new Error('Invalid canonicalContent Base64');
  const decoded = atob(value);
  if (btoa(decoded) !== value) throw new Error('Noncanonical Base64 padding bits');
  return Object.freeze(Array.from(decoded, (character) => character.charCodeAt(0)));
}
export function parseMissionTemplateWire(
  value: unknown,
  expected: ExactPin,
): MissionTemplateReadResult {
  keys(value, ['status', 'query', 'snapshot']);
  const query = pin(value.query);
  if (!samePin(query, expected)) throw new Error('Query identity mismatch');
  if (typeof value.status !== 'string' || !readStatuses.some((status) => status === value.status))
    throw new Error('Invalid read status');
  if (value.status !== 'VERIFIED') {
    if (value.snapshot !== null) throw new Error('Failed read carries snapshot');
    return missionTemplateReadResult(
      value.status as Exclude<MissionTemplateReadResult['status'], 'VERIFIED'>,
      query,
      null,
    );
  }
  keys(value.snapshot, ['pin', 'canonicalContent']);
  const snapshotPin = pin(value.snapshot.pin);
  if (!samePin(query, snapshotPin)) throw new Error('Snapshot identity mismatch');
  const canonicalContent = bytes(value.snapshot.canonicalContent);
  const decoded: unknown = JSON.parse(
    new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(canonicalContent)),
  );
  keys(decoded, ['body', 'canonicalizationVersion', 'id', 'kind', 'schemaVersion', 'version']);
  for (const key of pinFields)
    if (key !== 'digest' && decoded[key] !== query[key])
      throw new Error('Content identity mismatch');
  if (typeof decoded.body !== 'object' || decoded.body === null || Array.isArray(decoded.body))
    throw new Error('Content body required');
  return missionTemplateReadResult('VERIFIED', query, { pin: snapshotPin, canonicalContent });
}
